import { useMemo } from 'react'

import { useGisIssues } from './useGisIssues'
import { useGisSites } from './useGisSites'
import { useResolutionTargets } from './useResolutionTargets'
import { coverageSegments, severitySegments, type PieSegment } from '../lib/composition'
import { issueRiskSort, issueSignals, type IssueSignals } from '../lib/issue-board'
import { ageDays, isActive } from '../lib/issues'

import type { GisIssue, GisOverview, GisScope, GisSite, MandalAggregate, SiteStatus } from '../api/types'
import type { ResolutionTargets } from '../lib/resolution-targets'

/** First→last movement of a trend series (null when the window has <2 real points). */
function seriesDelta(values: (number | null)[]): number | null {
  const real = values.filter((v): v is number => v !== null)
  if (real.length < 2) {
    return null
  }
  return Math.round((real[real.length - 1]! - real[0]!) * 10) / 10
}

/** Everything the dashboard modules render — derived once, shared by the Dashboard tab AND the
 *  per-board dashboard overlays so the two surfaces can never disagree. */
export interface DashboardInsights {
  scopedMandals: MandalAggregate[]
  scopedSites: GisSite[]
  /** Every issue in the window's scoped feed (open + closed) — the base for filter-reactive KPIs. */
  scopedIssues: GisIssue[]
  openIssues: GisIssue[]
  /** Open issues in the board's risk order (breaching → critical → oldest). */
  rankedOpen: GisIssue[]
  signals: IssueSignals
  /** Every issue in the window's scoped feed (open + closed) — the "Total issues" KPI. */
  totalIssues: number
  closedInPeriod: number
  oldestOpen: number
  sevSegments: PieSegment[]
  covSegments: PieSegment[]
  siteTotal: number
  surveyed: number
  overdueSites: number
  neverSurveyed: number
  coveragePct: number | null
  /** District-wide period movement; null under a mandal scope (trends aren't per-mandal — never lie). */
  coverageDelta: number | null
  issuesDelta: number | null
  /** True only on COLD loads (keepPreviousData keeps stale data up during scope switches). */
  issuesPending: boolean
  sitesPending: boolean
  targets: ResolutionTargets
  trends: GisOverview['trends']
  trendBuckets: string[]
  trendBucketSize: string
}

/**
 * Derive the dashboard view-model from the page's overview + the mandal scope. Open issues join to
 * mandals via each issue's site — the same join the boards use, so a scoped count here always
 * matches the board the shortcut lands on.
 */
export function useDashboardInsights(
  overview: GisOverview,
  scope: GisScope,
  activeMandals: readonly string[],
): DashboardInsights {
  const { data: issuesPage, isPending: issuesFetchPending } = useGisIssues(scope)
  const { data: sitesData, isPending: sitesFetchPending } = useGisSites(scope)
  const issuesPending = issuesFetchPending && issuesPage === undefined
  const sitesPending = sitesFetchPending && sitesData === undefined
  const targets = useResolutionTargets()

  const scopedMandals = useMemo(
    () =>
      activeMandals.length > 0
        ? overview.mandals.filter((m) => activeMandals.includes(m.name))
        : overview.mandals,
    [overview.mandals, activeMandals],
  )
  const scopedSites = useMemo(
    () =>
      activeMandals.length > 0
        ? overview.sites.filter((s) => activeMandals.includes(s.mandal))
        : overview.sites,
    [overview.sites, activeMandals],
  )

  const siteMandal = useMemo(() => new Map(overview.sites.map((s) => [s.id, s.mandal])), [overview.sites])
  const scopedIssues = useMemo(() => {
    const all = issuesPage?.issues ?? []
    if (activeMandals.length === 0) {
      return all
    }
    return all.filter((i) => {
      const mandal = i.siteId !== null ? siteMandal.get(i.siteId) : undefined
      return mandal !== undefined && activeMandals.includes(mandal)
    })
  }, [issuesPage, activeMandals, siteMandal])
  const openIssues = useMemo(() => scopedIssues.filter(isActive), [scopedIssues])
  const rankedOpen = useMemo(() => issueRiskSort(openIssues, targets), [openIssues, targets])
  const signals = useMemo(() => issueSignals(openIssues, targets), [openIssues, targets])
  const oldestOpen = useMemo(
    () => openIssues.reduce((max, i) => Math.max(max, ageDays(i.createdAt)), 0),
    [openIssues],
  )

  const sevSegments = useMemo(() => {
    const counts = { critical: 0, non_critical: 0 }
    for (const i of openIssues) {
      counts[i.severity] += 1
    }
    return severitySegments(counts)
  }, [openIssues])
  const covSegments = useMemo(() => coverageSegments(scopedMandals), [scopedMandals])
  const siteTotal = covSegments.reduce((a, s) => a + s.count, 0)
  const countOf = (key: SiteStatus): number => covSegments.find((s) => s.key === key)?.count ?? 0
  const surveyed = countOf('surveyed')

  const scoped = activeMandals.length > 0
  return {
    scopedMandals,
    scopedSites,
    scopedIssues,
    openIssues,
    rankedOpen,
    signals,
    totalIssues: scopedIssues.length,
    closedInPeriod: scopedIssues.length - openIssues.length,
    oldestOpen,
    sevSegments,
    covSegments,
    siteTotal,
    surveyed,
    overdueSites: countOf('overdue'),
    neverSurveyed: countOf('never_surveyed'),
    coveragePct: siteTotal > 0 ? Math.round((surveyed / siteTotal) * 100) : null,
    coverageDelta: scoped ? null : seriesDelta(overview.trends.coverage),
    issuesDelta: scoped ? null : seriesDelta(overview.trends.issues),
    issuesPending,
    sitesPending,
    targets,
    trends: overview.trends,
    trendBuckets: overview.trendBuckets,
    trendBucketSize: overview.trendBucketSize,
  }
}
